import { api, readTotalCount } from '../api/client';
import { API_ADMIN_REVIEW_URL, API_COLLECTIONS_URL, API_GLOBAL_COLLECTIONS_URL } from '../utils';

// Global collections (keepr-api docs/SCHEMA.md "Global collections"): the
// owner's publication routes, follows, the Discover listing, the links into
// global collections, and the reviewer's queue. Errors propagate: every
// caller shows its own words for a refusal.

const collectionUrl = id => `${API_COLLECTIONS_URL}/${id}`;

// ---- the owner ----

/** Submit for review: `{ listing: { title, summary, category?, tags? }, allowExport?, allowFollow? }`. */
export async function submitForReview(collectionId, body) {
	const { data } = await api.post(`${collectionUrl(collectionId)}/publication/submit`, body);
	return data;
}

export async function withdrawSubmission(collectionId) {
	const { data } = await api.post(`${collectionUrl(collectionId)}/publication/withdraw`);
	return data;
}

/** `{ listing?, allowExport?, allowFollow? }` in any live state. */
export async function patchPublication(collectionId, body) {
	const { data } = await api.patch(`${collectionUrl(collectionId)}/publication`, body);
	return data;
}

/** The owner retires their own: `{ retireAt, path? }`. */
export async function retireOwnCollection(collectionId, body) {
	const { data } = await api.post(`${collectionUrl(collectionId)}/publication/retire`, body);
	return data;
}

/** `{ collections, items, threshold, suggestedPath, rows? }` — rows only for a reviewer. */
export async function getReferrers(collectionId) {
	const { data } = await api.get(`${collectionUrl(collectionId)}/referrers`);
	return data;
}

// ---- consumers ----

export async function followCollection(collectionId) {
	const { data } = await api.post(`${collectionUrl(collectionId)}/follow`);
	return data;
}

export async function unfollowCollection(collectionId) {
	const { data } = await api.delete(`${collectionUrl(collectionId)}/follow`);
	return data;
}

/** `{ rows, total }` of the global collections (and keepr archives) the caller reads. */
export async function listGlobalCollections({ q, skip = 0, limit = 100 } = {}) {
	const params = { skip, limit };
	if (q) params.q = q;
	const { data, headers } = await api.get(API_GLOBAL_COLLECTIONS_URL, { params });
	const rows = Array.isArray(data) ? data : [];
	return { rows, total: readTotalCount(headers, rows.length) };
}

/** The global collections a collection's own cards look up into (manage). */
export async function getGlobalSources(collectionId) {
	const { data } = await api.get(`${collectionUrl(collectionId)}/global-sources`);
	return Array.isArray(data) ? data : [];
}

/** `{ sourceCollectionId, itemIds? }` → the copy report. */
export async function copyReferences(collectionId, body) {
	const { data } = await api.post(`${collectionUrl(collectionId)}/copy-references`, body);
	return data;
}

/** "1 file" / "3 files". */
export const fileCount = n => `${n} file${n === 1 ? '' : 's'}`;

/**
 * The files half of a copy report, as short parts for a "·" summary
 * (keepr-api PR 10d, owner ruling G6): each copy carries its record's files
 * where this collection allows attachments — `attachmentsCarried` — and
 * counts the rest in `attachmentsSkipped`; `attachmentsOff` says the reason
 * was this collection's own switch (anything else — a file the source does
 * not let you download, or one since deleted — is not ours to name).
 */
export function copiedFilesParts(report) {
	const carried = report?.attachmentsCarried || 0;
	const skipped = report?.attachmentsSkipped || 0;
	const parts = [];
	if (carried) parts.push(`${fileCount(carried)} copied`);
	if (skipped) parts.push(report?.attachmentsOff ? `${fileCount(skipped)} not copied: attachments are off in this collection` : `${fileCount(skipped)} could not be copied`);
	return parts;
}

/**
 * The owner's way back from the archive path (keepr-api delete audit F12):
 * copies the records the retirement MOVED into the keepr archive back into
 * this collection as new records — at most 500 a call, so the caller loops
 * while `remaining` > 0. → the copy report plus `{ archiveId, total, remaining }`.
 */
export async function copyArchiveBack(collectionId) {
	const { data } = await api.post(`${collectionUrl(collectionId)}/copy-archive-back`, {});
	return data;
}

// ---- reviewers ----

/** `{ rows, total }` of one queue group (absent: everything not retired). */
export async function listReviewQueue({ group, q, skip = 0, limit = 50 } = {}) {
	const params = { skip, limit };
	if (group) params.group = group;
	if (q) params.q = q;
	const { data, headers } = await api.get(API_ADMIN_REVIEW_URL, { params });
	const rows = Array.isArray(data) ? data : [];
	return { rows, total: readTotalCount(headers, rows.length) };
}

export async function getReview(collectionId) {
	const { data } = await api.get(`${API_ADMIN_REVIEW_URL}/${collectionId}`);
	return data;
}

/** verb: publish | request-changes | decline | re-review | retire | cancel-retirement */
export async function reviewAction(collectionId, verb, body = {}) {
	const { data } = await api.post(`${API_ADMIN_REVIEW_URL}/${collectionId}/${verb}`, body);
	return data;
}

// ---- words ----

export const PUBLICATION_STATE_LABELS = {
	submitted: 'Submitted',
	'changes-requested': 'Changes requested',
	published: 'Published',
	retiring: 'Retiring',
	retired: 'Retired',
};

/** The refusal the server gave, in words a person can act on. */
export function publicationRefusal(error, fallback = 'That did not work. Trying again may help.') {
	const body = error?.response?.data || {};
	switch (body.code) {
		case 'publication_state': return 'This collection has moved on since the page loaded. Reload to see where it stands.';
		case 'retire_too_soon': return 'A global collection retires at least 30 days out, so readers have time to copy what they link to.';
		case 'personal_collection_locked': return 'Your personal collection cannot become a global collection.';
		case 'collection_not_active': return 'Unarchive the collection first.';
		case 'category_not_listed': return body.message || 'Choose one of Discover\'s categories.';
		case 'invalid_listing': return body.message || 'The listing needs a title and a summary.';
		case 'email_unverified': return 'Confirm your email address first.';
		case 'follow_not_allowed': return 'This collection cannot be followed.';
		case 'collection_retiring': return 'This collection is retiring; it can no longer be followed.';
		case 'note_required': return 'Say why — a note is required for this.';
		case 'review_own_collection': return 'You cannot review your own collection. Another reviewer or an admin decides.';
		case 'export_not_allowed': return 'The owner of this global collection does not allow exports.';
		case 'path_required': return 'Choose what happens to the records other collections link to.';
		case 'no_archive': return 'This collection did not retire to the keepr archive, so there is nothing to copy back.';
		case 'invalid_key': return 'That change could not be saved.';
		default: return body.message || fallback;
	}
}
